import React, { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
    IconButton,
    Tooltip,
} from "@mui/material";
import SwitchCameraIcon from "@mui/icons-material/SwitchCamera";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import "./BarcodeScanner.css";

interface BarcodeScannerProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (result: string | null) => void;
}

type Facing = "environment" | "user";
const REGION_ID = "html5qr-region";
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ isOpen, onClose, onConfirm }) => {
    const qrRef = useRef<Html5Qrcode | null>(null);

    const startingRef = useRef(false);
    const runningRef = useRef(false);
    const mountedRef = useRef(false);
    const pendingStartRef = useRef<{ cameraId?: string; facing?: Facing } | null>(null);

    const [result, setResult] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [cameras, setCameras] = useState<Array<{ id: string; label: string }>>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string>("");
    const [facing, setFacing] = useState<Facing>("environment");

    // ✅ force remount du container (obligatoire pour hard reset sur mobile)
    const [regionKey, setRegionKey] = useState(0);

    const safeSetError = (msg: string | null) => mountedRef.current && setErrorMsg(msg);
    const safeSetResult = (v: string | null) => mountedRef.current && setResult(v);

    const ensureInstance = useCallback(() => {
        if (!qrRef.current) {
            qrRef.current = new Html5Qrcode(REGION_ID, { verbose: false } as any);
        }
    }, []);

    // ✅ stop "hard" : stop + clear (libère mieux la caméra)
    const stopHard = useCallback(async () => {
        try {
            if (qrRef.current && runningRef.current) {
                try {
                    await qrRef.current.stop();
                } catch {}
                try {
                    await qrRef.current.clear();
                } catch {}
            }
        } finally {
            runningRef.current = false;
            startingRef.current = false;
            await sleep(250); // important mobile
        }
    }, []);

    const loadCameras = useCallback(async () => {
        try {
            const list = await Html5Qrcode.getCameras();
            const cams = list.map((c) => ({ id: c.id, label: c.label || `Caméra ${c.id}` }));
            setCameras(cams);
            return cams;
        } catch {
            setCameras([]);
            return [];
        }
    }, []);

    const start = useCallback(
        async (opts?: { cameraId?: string; facing?: Facing; retry?: number }): Promise<void> => {
            if (startingRef.current) {
                pendingStartRef.current = opts ?? {};
                safeSetError(null);
                return;
            }

            startingRef.current = true;
            safeSetError(null);
            safeSetResult(null);

            ensureInstance();

            // 🔥 important : libérer correctement avant restart
            await stopHard();

            const cameraId = opts?.cameraId ?? "";
            const wantedFacing = opts?.facing ?? facing;
            const retry = opts?.retry ?? 0;

            try {
                const config = {
                    fps: 12,
                    qrbox: { width: 280, height: 180 },
                    aspectRatio: 1.777,
                    disableFlip: true,
                };

                const onSuccess = async (decodedText: string) => {
                    safeSetResult(decodedText);
                    await stopHard();
                };

                const onError = () => {
                    // silence
                };

                // ✅ Start
                if (cameraId) {
                    await qrRef.current!.start(cameraId, config as any, onSuccess, onError);
                } else {
                    await qrRef.current!.start({ facingMode: wantedFacing }, config as any, onSuccess, onError);
                }

                // ✅ si on arrive ici, la caméra est bien démarrée => on efface toute erreur
                runningRef.current = true;
                safeSetError(null);

                // charger cams après permission
                if (!cameras.length) await loadCameras();
            } catch (e: any) {
                runningRef.current = false;

                const raw = String(e?.message || e || "");
                const msg = raw.toLowerCase();
                const name = String(e?.name || "").toLowerCase();

                const isAbort = msg.includes("aborted") || msg.includes("aborterror") || name.includes("abort");
                const isNotReadable =
                    msg.includes("notreadableerror") ||
                    msg.includes("could not start video source") ||
                    msg.includes("trackstart") ||
                    name.includes("notreadable");
                const isNotAllowed =
                    msg.includes("notallowederror") || msg.includes("permission") || msg.includes("denied") || name.includes("notallowed");
                const isOverconstrained =
                    msg.includes("overconstrainederror") || msg.includes("constraint") || name.includes("overconstrained");

                // ✅ retry 1 fois
                if ((isAbort || isNotReadable) && retry < 1) {
                    startingRef.current = false;
                    await sleep(400);
                    return start({ cameraId, facing: wantedFacing, retry: retry + 1 });
                }

                if (isNotAllowed) {
                    safeSetError("Permission caméra refusée. Autorise la caméra puis réessaie (HTTPS obligatoire).");
                } else if (isOverconstrained) {
                    safeSetError("Caméra demandée non disponible. Choisis une caméra via le menu.");
                } else if (isNotReadable) {
                    safeSetError("Caméra indisponible (déjà utilisée). Ferme les autres apps/onglets puis réessaie.");
                } else if (isAbort) {
                    safeSetError("La caméra a été interrompue. Clique sur Reset puis réessaie.");
                } else {
                    // ✅ message plus honnête (pas toujours https)
                    safeSetError("Impossible de démarrer la caméra. Essaie Reset, puis sélectionne une caméra (fallback).");
                }
            } finally {
                startingRef.current = false;

                const pending = pendingStartRef.current;
                if (pending) {
                    pendingStartRef.current = null;
                    await sleep(150);
                    await start(pending);
                }
            }
        },
        [ensureInstance, stopHard, facing, cameras.length, loadCameras]
    );

    // ✅ hard reset = recréer instance + remount DOM + restart
    const hardReset = useCallback(async () => {
        safeSetError(null);
        safeSetResult(null);

        await stopHard();

        // tuer instance
        qrRef.current = null;

        // remonter DOM container
        setRegionKey((k) => k + 1);
        await sleep(250);

        await loadCameras();
        setSelectedCameraId("");
        setFacing("environment");

        await start({ facing: "environment", cameraId: "", retry: 0 });
    }, [loadCameras, start, stopHard]);

    // mount guard
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // fermeture
    useEffect(() => {
        if (!isOpen) {
            (async () => {
                await stopHard();
                safeSetResult(null);
                safeSetError(null);
            })();
        }
    }, [isOpen, stopHard]);

    const handleCloseAll = async () => {
        await stopHard();
        safeSetResult(null);
        safeSetError(null);
        onClose();
    };

    const handleConfirm = async () => {
        await stopHard();
        onConfirm(result);
    };

    // ✅ Rescanner = hard reset (pas juste start)
    const handleRescan = async () => {
        await hardReset();
    };

    const handleToggleFacing = async () => {
        const next: Facing = facing === "environment" ? "user" : "environment";
        setFacing(next);
        setSelectedCameraId("");
        await start({ facing: next, cameraId: "" });
    };

    const handleSelectCamera = async (id: string) => {
        setSelectedCameraId(id);
        await start({ cameraId: id });
    };

    // ✅ Reset = hard reset
    const handleResetScanner = async () => {
        await hardReset();
    };

    return (
        <>
            <Dialog
                open={isOpen && !result}
                onClose={handleCloseAll}
                maxWidth="sm"
                fullWidth
                TransitionProps={{
                    onEntered: () => {
                        setTimeout(() => start({ facing: "environment", cameraId: "" }), 250);
                    },
                    onExit: () => {
                        stopHard();
                    },
                }}
            >
                <DialogTitle>Scanner code-barres</DialogTitle>

                <DialogContent>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={facing === "environment" ? "Basculer caméra (arrière)" : "Basculer caméra (avant)"}>
                                <IconButton onClick={handleToggleFacing} color="primary" disabled={startingRef.current}>
                                    <SwitchCameraIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Reset / actualiser le scanner">
                                <IconButton onClick={handleResetScanner} color="primary" disabled={startingRef.current}>
                                    <RestartAltIcon />
                                </IconButton>
                            </Tooltip>

                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="cam-select-label">Caméra (fallback)</InputLabel>
                                <Select
                                    labelId="cam-select-label"
                                    label="Caméra (fallback)"
                                    value={selectedCameraId}
                                    onChange={(e) => handleSelectCamera(String(e.target.value))}
                                    disabled={!cameras.length || startingRef.current}
                                >
                                    {cameras.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {errorMsg && (
                            <Typography color="error" variant="body2">
                                {errorMsg}
                            </Typography>
                        )}

                        <div className="video-container">
                            <div className="animated-rectangle" />
                            <div className="scanning-label">Scanning...</div>

                            {/* ✅ remount via key */}
                            <div key={regionKey} id={REGION_ID} />
                        </div>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseAll} variant="outlined">
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isOpen && !!result} onClose={handleCloseAll}>
                <DialogTitle>Code détecté</DialogTitle>
                <DialogContent>
                    <Typography>{result}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirm} variant="contained">
                        Confirmer
                    </Button>
                    <Button onClick={handleRescan} variant="outlined">
                        Rescanner
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BarcodeScanner;
